/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { ICampaignRequest } from '@dataTypes/marketing.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { ChangedEventArgs } from '@syncfusion/ej2-react-calendars'
import {
  DragEventArgs,
  PopupOpenEventArgs,
  ScheduleComponent,
} from '@syncfusion/ej2-react-schedule'
import { addMonths, format, subMonths } from 'date-fns'
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ITemplateProps } from './CampaignCalendar.component'
import {
  getEndOfDay,
  getLocalDayKey,
  getStartOfDay,
  shuffle,
} from '@utils/marketing.utils'
import {
  CHILD_HEIGHT,
  HEAD_HEIGHT,
  PAGE_CAPACITY,
  PALETTE,
} from '@constants/Marketing'
import { deleteCampaign, updateCampaign } from '@store/actions'
import moment from 'moment'

interface ICampaignCalendarHooks {
  calendarContainerRef: RefObject<HTMLDivElement>
  campaignData: ITemplateProps[]
  handleNextMonth: () => void
  handlePrevMonth: () => void
  handlePopupOpen: (args: PopupOpenEventArgs) => void
  onCancelReschedule: () => void
  onConfirmDelete: () => void
  onConfirmReschedule: () => void
  onDateChange: (args: ChangedEventArgs) => void
  onDrag: (args: any) => void
  onDragStart: (args: any) => void
  onDragStop: (args: any) => void
  onDropDownSelect: (args: any, campaign: ICampaignRequest) => void
  onEventRendered: (args: any) => void
  onHideModal: () => void
  onResizing: (args: any) => void
  renderDayHeader: (args: any) => void
  rescheduleTime: string
  onDataBound: () => void
  scheduleRef: RefObject<ScheduleComponent>
  selectedCampaign: ICampaignRequest | null
  selectedDate: Date
  setRescheduleTime: Dispatch<SetStateAction<string>>
  setSelectedCampaign: Dispatch<SetStateAction<ICampaignRequest | null>>
  showCalendar: boolean
  showDeleteModal: boolean
  showEditCampaignModal: boolean
  showRescheduleDragModal: boolean
  showRescheduleDropDownModal: boolean
  toggleCalendar: () => void
  visibleEvents: ITemplateProps[]
}

type PagedTemplateEvent = ITemplateProps & {
  dayKey: string
  pageIndex: number
}

export const useCampaignCalendar = (
  campaignsOverride?: ICampaignRequest[],
): ICampaignCalendarHooks => {
  const dispatch = useAppDispatch()
  const reduxCampaigns: ICampaignRequest[] = useAppSelector(
    (state) => state.marketing.campaigns,
  )
  const sourceCampaigns =
    campaignsOverride !== undefined ? campaignsOverride : reduxCampaigns
  const [localCampaigns, setLocalCampaigns] =
    useState<ICampaignRequest[]>(sourceCampaigns)
  const [showCalendar, setShowCalendar] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false)
  const [showRescheduleDragModal, setShowRescheduleDragModal] = useState(false)
  const [showRescheduleDropDownModal, setShowRescheduleDropDownModal] =
    useState(false)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [rescheduleTime, setRescheduleTime] = useState<string>('')
  const [selectedCampaign, setSelectedCampaign] =
    useState<ICampaignRequest | null>(null)
  const [pageIndexByDay, setPageIndexByDay] = useState<Map<string, number>>(
    new Map(),
  )
  const pageIndexByDayRef = useRef<Map<string, number>>(new Map())
  const calendarContainerRef = useRef<HTMLDivElement>(null)
  const scheduleRef = useRef<ScheduleComponent>(null)
  const colorById = useRef(new Map<string, string>())
  const deckRef = useRef<string[]>(shuffle(PALETTE))

  const getColorFor = (key: string): string => {
    if (colorById.current.has(key)) return colorById.current.get(key)!

    if (deckRef.current.length === 0) deckRef.current = shuffle(PALETTE)

    const color = deckRef.current.pop()!
    colorById.current.set(key, color)
    return color
  }

  const handlePrevMonth = (): void => {
    setSelectedDate(subMonths(selectedDate, 1))
  }

  const handleNextMonth = (): void => {
    setSelectedDate(addMonths(selectedDate, 1))
  }

  const handlePopupOpen = (args: PopupOpenEventArgs): void => {
    if (args.type === 'Editor') {
      args.cancel = true
    }

    if (args.type === 'QuickInfo') {
      args.cancel = true
    }
  }

  const toggleCalendar = (): void => {
    setShowCalendar(!showCalendar)
  }

  const onDateChange = (args: ChangedEventArgs): void => {
    setSelectedDate(args.value as Date)
  }

  const splitCampaignTile = (c: ICampaignRequest): ITemplateProps[] => {
    if (!c.start_date || !c.end_date || !c.start_time || !c.end_time) return []
    const tileEvents: ITemplateProps[] = []
    const campaignStartDate = new Date(c.start_date)
    const [startHours, startMinutes] = c.start_time.split(':').map(Number)
    campaignStartDate.setHours(startHours, startMinutes)
    const campaignEndDate = new Date(c.end_date)
    const [endHours, endMinutes] = c.end_time.split(':').map(Number)
    campaignEndDate.setHours(endHours, endMinutes)

    let isHead = true

    const addTile = (_startDate: Date): void => {
      const _startTime = getStartOfDay(new Date(_startDate))
      const _endTime = getEndOfDay(new Date(_startDate))
      tileEvents.push({
        id: `${c.campaign_id}-${_startTime.getTime()}`,
        headId: c.campaign_id as string,
        isHead,
        subject: c.name,
        startTime: _startTime,
        endTime: _endTime,
        color: getColorFor(c.campaign_id as string),
        campaign: c,
      })
      isHead = false
    }
    let currentDate = campaignStartDate
    while (currentDate <= campaignEndDate) {
      addTile(currentDate)
      currentDate = moment(currentDate).add(1, 'day').toDate()
    }
    return tileEvents
  }

  const campaignData = useMemo(
    () => localCampaigns.flatMap(splitCampaignTile),
    [localCampaigns],
  )

  const { eventsWithPages, totalPagesByDay } = useMemo(() => {
    const groupedByDay = new Map<string, ITemplateProps[]>()

    for (const templateEvent of campaignData) {
      const startDate = new Date(templateEvent.startTime as Date)
      const dayKey = getLocalDayKey(startDate)
      const bucket = groupedByDay.get(dayKey) ?? []
      bucket.push(templateEvent)
      groupedByDay.set(dayKey, bucket)
    }

    const annotated: PagedTemplateEvent[] = []
    const pagesPerDay = new Map<string, number>()

    groupedByDay.forEach((eventsOfDay, dayKey) => {
      const sortedEvents = eventsOfDay
        .slice()
        .sort((left, right) =>
          left.isHead === right.isHead
            ? new Date(left.startTime as Date).getTime() -
              new Date(right.startTime as Date).getTime()
            : left.isHead
              ? -1
              : 1,
        )

      let currentPageIndex = 0
      let usedCapacity = 0

      for (const eventItem of sortedEvents) {
        const weight = eventItem.isHead ? HEAD_HEIGHT : CHILD_HEIGHT
        const fits =
          usedCapacity === 0
            ? weight <= PAGE_CAPACITY
            : usedCapacity + weight <= PAGE_CAPACITY

        if (!fits) {
          currentPageIndex += 1
          usedCapacity = 0
        }

        annotated.push({ ...eventItem, dayKey, pageIndex: currentPageIndex })
        usedCapacity += weight
      }

      pagesPerDay.set(dayKey, currentPageIndex + 1)
    })

    return { eventsWithPages: annotated, totalPagesByDay: pagesPerDay }
  }, [campaignData])

  const visibleEvents = useMemo(() => {
    return eventsWithPages.filter((eventItem) => {
      const currentIndex = pageIndexByDay.get(eventItem.dayKey) ?? 0
      return currentIndex === eventItem.pageIndex
    })
  }, [eventsWithPages, pageIndexByDay])

  const copyTileIntoClone = (sourceEl: HTMLElement): void => {
    const host = scheduleRef.current?.element as HTMLElement | undefined
    if (!host) return

    const clone = host.querySelector(
      '.e-schedule-event-clone',
    ) as HTMLElement | null
    if (!clone) return

    const srcTile = sourceEl.querySelector(
      '.cc-event-tile',
    ) as HTMLElement | null
    const cloneDetails = clone.querySelector(
      '.e-appointment-details',
    ) as HTMLElement | null

    if (!cloneDetails || !srcTile) return

    if (!cloneDetails.querySelector('.cc-event-tile')) {
      cloneDetails.innerHTML = ''
      cloneDetails.appendChild(srcTile.cloneNode(true))
    }

    const classKeep = [
      'cc-sc-head',
      'gray',
      'red',
      'orange',
      'yellow',
      'pale-green',
      'vivid-green',
      'teal',
      'blue',
      'indigo',
      'violet',
    ]
    classKeep.forEach((cls) => {
      if (sourceEl.classList.contains(cls)) clone.classList.add(cls)
    })

    clone.style.opacity = '1'
    clone.style.background = 'transparent'
  }

  const onDrag = (args: any): void => {
    const el = args.element as HTMLElement | null
    if (!el) return
    requestAnimationFrame(() => copyTileIntoClone(el))
  }

  const onDragStart = (args: any): void => {
    const data = args.data as ITemplateProps
    if (!data.isHead) {
      args.cancel = true
    }

    setSelectedCampaign(data.campaign)
    requestAnimationFrame(() => copyTileIntoClone(args.element as HTMLElement))
  }

  const onDragStop = (args: DragEventArgs): void => {
    if (
      selectedCampaign &&
      selectedCampaign.campaign_id &&
      selectedCampaign.start_date
    ) {
      const _new = format(args.data.startTime, 'MM/dd/yyyy')
      const _current = format(selectedCampaign.start_date, 'MM/dd/yyyy')
      if (_new !== _current) {
        const newCampaigns = localCampaigns.map((campaign) => {
          if (campaign.campaign_id === selectedCampaign.campaign_id) {
            if (
              !campaign.start_date ||
              !campaign.end_date ||
              !campaign.start_time ||
              !campaign.end_time
            )
              return campaign
            const duration = moment(selectedCampaign.end_date)
              .startOf('day')
              .diff(moment(selectedCampaign.start_date).startOf('day'), 'days')
            const [startHours, startMinutes] = campaign.start_time
              .split(':')
              .map(Number)
            const newStartDate = new Date(args.data.startTime)
            newStartDate.setHours(startHours, startMinutes)
            const [endHours, endMinutes] = campaign.end_time
              .split(':')
              .map(Number)
            const newEndDate = moment(newStartDate)
              .add(duration, 'day')
              .toDate()
            newEndDate.setHours(endHours, endMinutes)
            return {
              ...campaign,
              start_date: newStartDate.toLocaleString(),
              end_date: newEndDate.toLocaleString(),
            }
          }
          return campaign
        })
        setLocalCampaigns(newCampaigns)
        setShowRescheduleDragModal(true)
      }
    }
  }

  const reflowMonthCells = (): void => {
    const host = scheduleRef.current?.element as HTMLElement | undefined
    if (!host) return

    const wrappers = host.querySelectorAll(
      '.e-month-view .e-appointment-wrapper',
    ) as NodeListOf<HTMLElement>

    const GAP = 4 // pixels between tiles

    wrappers.forEach((wrap) => {
      let y = 0
      const items = Array.from(
        wrap.querySelectorAll('.e-appointment'),
      ) as HTMLElement[]
      const workCell = wrap.closest('.e-work-cells') as HTMLElement | null
      const cellWidth =
        workCell?.clientWidth ?? wrap.parentElement?.clientWidth ?? 0
      const targetWidth = Math.max(cellWidth - 16, 0)

      items.forEach((el) => {
        el.style.top = `${y}px` // position based on accumulated height
        el.style.width = `${targetWidth}px`
        el.style.boxSizing = 'border-box'
        const details = el.querySelector(
          '.e-appointment-details',
        ) as HTMLElement | null
        if (details) {
          details.style.width = '100%'
          details.style.maxWidth = '100%'
          details.style.boxSizing = 'border-box'
        }
        y += el.offsetHeight + GAP // add actual height + gap
      })
    })
  }

  const handleChangePage = (
    change: 'prev' | 'next',
    dayKey: string,
    label: HTMLSpanElement,
  ): void => {
    const totalPages = totalPagesByDay.get(dayKey) ?? 1
    const currentIndex = pageIndexByDayRef.current.get(dayKey) ?? 0
    const newIndex =
      change === 'prev'
        ? currentIndex - 1 < 0
          ? totalPages - 1
          : currentIndex - 1
        : currentIndex + 1 >= totalPages
          ? 0
          : currentIndex + 1
    const newPageIndexByDay = new Map(pageIndexByDayRef.current)
    newPageIndexByDay.set(dayKey, newIndex)
    pageIndexByDayRef.current = newPageIndexByDay
    setPageIndexByDay(newPageIndexByDay)
    label.textContent = `${newIndex + 1}`
    requestAnimationFrame(reflowMonthCells)
  }

  const renderDayHeader = (args: any): void => {
    if (args.elementType === 'monthDay') {
      const abbreviated = args.element.innerText.slice(0, 3).toUpperCase()
      args.element.innerText = abbreviated
    } else if (args.elementType === 'monthCells') {
      const display = new Date(args.date)
      args.element.firstElementChild.innerText = format(display, 'd')

      const cellDate = new Date(args.date)
      const dayKey = getLocalDayKey(cellDate)

      args.element.firstElementChild.innerHTML = String(cellDate.getDate())

      const existing = args.element.querySelector('.cc-day-pager')
      if (existing) existing.remove()

      const totalPages = totalPagesByDay.get(dayKey) ?? 1
      if (totalPages <= 1) return

      const pager = document.createElement('div')
      pager.className = 'cc-day-pager'

      const prevBtn = document.createElement('button')
      prevBtn.className = 'cc-day-page-btn cc-day-page-btn-prev'
      prevBtn.textContent = '‹'

      const label = document.createElement('span')
      const currentIndex = pageIndexByDay.get(dayKey) ?? 0
      label.className = 'cc-day-page-label'
      const currentPageLabel = document.createElement('span')
      currentPageLabel.textContent = `${currentIndex + 1}`
      const dividerLabel = document.createElement('span')
      dividerLabel.textContent = '/'
      const totalPageLabel = document.createElement('span')
      totalPageLabel.textContent = `${totalPages}`
      label.appendChild(currentPageLabel)
      label.appendChild(dividerLabel)
      label.appendChild(totalPageLabel)

      const nextBtn = document.createElement('button')
      nextBtn.className = 'cc-day-page-btn cc-day-page-btn-next'
      nextBtn.textContent = '›'

      prevBtn.onclick = (evt) => {
        evt.stopPropagation()
        handleChangePage('prev', dayKey, currentPageLabel)
      }
      nextBtn.onclick = (evt) => {
        evt.stopPropagation()
        handleChangePage('next', dayKey, currentPageLabel)
      }

      args.element.appendChild(prevBtn)
      args.element.appendChild(label)
      args.element.appendChild(nextBtn)
    }
  }

  const onDataBound = (): void => {
    const host = scheduleRef.current?.element as HTMLElement
    const rows =
      host.querySelectorAll('.e-month-view .e-content-table tbody tr').length ||
      6
    host.style.setProperty('--monthHeaderH', '48px')
    host.style.setProperty('--monthWeeks', String(rows))

    const tooltip = document.querySelector('.e-schedule-event-tooltip')
    if (tooltip) {
      tooltip.classList.add('campaign-tooltip-wrap')
    }
    requestAnimationFrame(reflowMonthCells)
  }

  const onEventRendered = (args: any): void => {
    const data = args.data as ITemplateProps
    if (data.isHead) {
      args.element.classList.add('cc-sc-head', data.color)
      args.element.style.backgroundColor = data.color
    } else {
      args.element.classList.add('cc-sc-body', data.color)
    }
    requestAnimationFrame(reflowMonthCells)
  }

  const onDropDownSelect = (args: any, campaign: ICampaignRequest): void => {
    setSelectedCampaign(campaign)
    const action = args.item.id
    switch (action) {
      case 'edit':
        setShowEditCampaignModal(true)
        break
      case 'delete':
        setShowDeleteModal(true)
        break
      case 'reschedule':
        setShowRescheduleDropDownModal(true)
        break
    }
  }
  const onCancelReschedule = (): void => {
    setShowRescheduleDragModal(false)
    setLocalCampaigns(sourceCampaigns)
  }

  const onHideModal = (): void => {
    setShowDeleteModal(false)
    setShowEditCampaignModal(false)
    setShowRescheduleDragModal(false)
    setShowRescheduleDropDownModal(false)
  }

  const onConfirmReschedule = (): void => {
    if (selectedCampaign && selectedCampaign.campaign_id) {
      dispatch(
        updateCampaign({
          campaign_id: selectedCampaign.campaign_id,
          data: {
            timezone: selectedCampaign.timezone,
            ...(selectedCampaign.start_date
              ? {
                  start_date: format(selectedCampaign.start_date, 'MM/dd/yyyy'),
                }
              : {}),
            start_time: selectedCampaign.start_time,
            ...(selectedCampaign.end_date
              ? {
                  end_date: format(selectedCampaign.end_date, 'MM/dd/yyyy'),
                }
              : {}),
            end_time: selectedCampaign.end_time,
            ...(rescheduleTime !== selectedCampaign.start_time && {
              start_time: rescheduleTime,
            }),
          },
        }),
      ).then(() => {
        setShowRescheduleDragModal(false)
      })
    }
  }

  const onConfirmDelete = (): void => {
    if (selectedCampaign && selectedCampaign.campaign_id) {
      dispatch(deleteCampaign(selectedCampaign.campaign_id))
    }
    setSelectedCampaign(null)
  }

  const onResizing = (args: any): void => {
    args.cancel = true
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        showCalendar &&
        calendarContainerRef.current &&
        !calendarContainerRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCalendar])

  useEffect(() => {
    if (selectedCampaign) {
      const _campaign = localCampaigns.find(
        (c) => c.campaign_id === selectedCampaign.campaign_id,
      )
      if (_campaign) setSelectedCampaign(_campaign)
    } else setSelectedCampaign(null)
  }, [localCampaigns])

  useEffect(() => {
    if (selectedCampaign && selectedCampaign.start_time)
      setRescheduleTime(selectedCampaign.start_time)
    else setRescheduleTime('')
  }, [selectedCampaign])

  useEffect(() => {
    const host = scheduleRef.current?.element as HTMLElement | undefined
    if (!host) return

    const triggerReflow = (): void => {
      requestAnimationFrame(reflowMonthCells)
    }

    const resizeObserver = new ResizeObserver(() => {
      triggerReflow()
    })
    resizeObserver.observe(host)

    window.addEventListener('resize', triggerReflow)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', triggerReflow)
    }
  }, [])

  useEffect(() => {
    setLocalCampaigns(sourceCampaigns)
  }, [sourceCampaigns])

  return {
    calendarContainerRef,
    campaignData,
    handleNextMonth,
    handlePrevMonth,
    handlePopupOpen,
    onConfirmDelete,
    onConfirmReschedule,
    onHideModal,
    onCancelReschedule,
    onDateChange,
    onDropDownSelect,
    onEventRendered,
    renderDayHeader,
    onDataBound,
    onDrag,
    onDragStart,
    onDragStop,
    onResizing,
    rescheduleTime,
    scheduleRef,
    setRescheduleTime,
    selectedDate,
    selectedCampaign,
    setSelectedCampaign,
    showCalendar,
    showDeleteModal,
    showEditCampaignModal,
    showRescheduleDragModal,
    showRescheduleDropDownModal,
    toggleCalendar,
    visibleEvents,
  }
}
